/**
 * FORKED FROM SHADCN ON PURPOSE, AND ON ONE LINE.
 *
 * `src/ui` is generated output and the rule of this tree is to compose it, not
 * to edit it: an edit has to be reconciled by hand on every
 * `shadcn add --diff`. That rule is why the close button read «Close» in every
 * language — the strings in here are not translated, deliberately.
 *
 * It stops being the right answer on this one button. It has no visible text,
 * so the label *is* what a screen reader announces, and it is announced on
 * every dialog in both applications: renaming, moving, sharing, previewing,
 * inviting. One English word on every dialog of an Italian product is not a
 * detail of the design system, and passing the label down from each caller
 * instead would mean every dialog has to remember, with nothing able to tell
 * when one forgets (OPSSTD-7089).
 *
 * The cost is stated rather than hidden: this file is now a fork, and an
 * upgrade has to bring this back. The other untranslated strings in `src/ui`
 * are the same class of defect and are not fixed here.
 */

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { useTranslation } from '@repo/i18n'
import { XIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '#lib/utils'
import { Button } from '#ui/button'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
	return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
	return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
	return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
	return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({
	className,
	...props
}: DialogPrimitive.Backdrop.Props) {
	return (
		<DialogPrimitive.Backdrop
			className={cn(
				'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
				className
			)}
			data-slot='dialog-overlay'
			{...props}
		/>
	)
}

function DialogContent({
	className,
	children,
	showCloseButton = true,
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean
}) {
	const { t } = useTranslation()
	const closeLabel = t('shared:dialog.close')

	return (
		<DialogPortal>
			<DialogOverlay />
			<DialogPrimitive.Popup
				className={cn(
					'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
					className
				)}
				data-slot='dialog-content'
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						data-slot='dialog-close'
						render={
							<Button
								className='absolute top-4 right-4'
								size='icon-sm'
								variant='ghost'
							/>
						}
					>
						<XIcon />
						<span className='sr-only'>{closeLabel}</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Popup>
		</DialogPortal>
	)
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn('flex flex-col gap-2', className)}
			data-slot='dialog-header'
			{...props}
		/>
	)
}

function DialogFooter({
	className,
	showCloseButton = false,
	children,
	...props
}: React.ComponentProps<'div'> & {
	showCloseButton?: boolean
}) {
	return (
		<div
			className={cn(
				'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
				className
			)}
			data-slot='dialog-footer'
			{...props}
		>
			{children}
			{showCloseButton && (
				<DialogPrimitive.Close render={<Button variant='outline' />}>
					Close
				</DialogPrimitive.Close>
			)}
		</div>
	)
}

/**
 * Set apart from a field label by size and weight, not by a second typeface
 * (OPSSTD-5834): shadcn's `base-vega` gives the title `font-heading` and no
 * size, and here `--font-heading` is the body font, so a title read 14px at
 * the weight of the «Nome» under it. One place, so every dialog changes
 * together. Another line an upgrade has to bring back.
 */
function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
	return (
		<DialogPrimitive.Title
			className={cn(
				'font-heading text-base leading-snug font-semibold',
				className
			)}
			data-slot='dialog-title'
			{...props}
		/>
	)
}

function DialogDescription({
	className,
	...props
}: DialogPrimitive.Description.Props) {
	return (
		<DialogPrimitive.Description
			className={cn(
				'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
				className
			)}
			data-slot='dialog-description'
			{...props}
		/>
	)
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger
}
